/**
 * Defines distinct colors for different activation expressions
 * Used to visually distinguish bricks with the same activation expression
 */

export const ACTIVATION_COLORS = [
  // Primary colors with good contrast against white and good distinction from each other
  '#2E86C1', // Blue
  '#16A085', // Green
  '#D35400', // Orange
  '#8E44AD', // Purple
  '#C0392B', // Red
  '#27AE60', // Emerald
  '#F39C12', // Amber
  '#2C3E50', // Dark Blue
  '#E74C3C', // Light Red
  '#1ABC9C', // Turquoise
];

// Map to store previously assigned colors
const expressionColorMap: Map<string, string> = new Map();
let nextColorIndex = 0;

/**
 * Returns a consistent color for a given activation expression.
 * Uses a memory approach to ensure the same expression always gets the same color,
 * and assigns new colors sequentially.
 * 
 * @param activationExpression The activation expression to get a color for
 * @returns A hex color code from the ACTIVATION_COLORS array or undefined if no expression
 */
export const getActivationColor = (activationExpression: string | undefined): string | undefined => {
  if (!activationExpression) return undefined;

  const trimmedExpression = activationExpression.replaceAll(' ', '').trim().toLowerCase();
  
  // Check if we've already assigned a color to this expression
  if (expressionColorMap.has(trimmedExpression)) {
    return expressionColorMap.get(trimmedExpression);
  }
  
  // Assign the next available color
  const color = ACTIVATION_COLORS[nextColorIndex];
  
  // Store this assignment for future reference
  expressionColorMap.set(trimmedExpression, color);
  
  // Increment the color index and reset if we've used all colors
  nextColorIndex = (nextColorIndex + 1) % ACTIVATION_COLORS.length;
  
  return color;
};

/**
 * Resets the color assignment memory.
 * Useful for testing or if you want to start fresh.
 */
export const resetActivationColors = (): void => {
  expressionColorMap.clear();
  nextColorIndex = 0;
};